import dynamic from "next/dynamic";
import { useRouter } from "next/router";

import React, { useCallback, useState } from "react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";

import { Entity } from "models/entity";
import { twMerge } from "tailwind-merge";

import Modal from "components/Modal";
import SearchInput from "components/SearchInput";
import useMediaQuery from "hooks/useMediaQuery";
import { baseFetcher } from "utils/baseFetcher";
import { primerInsightsUrl } from "utils/primerInsightsUrl";

import { RecentPrimersSection } from "../AIWorkflowModalSections";

const InsightModal = dynamic(() => import("components/InsightModal"));

const EntityPrimerButton = ({ className }: { className?: string }) => {
  const router = useRouter();
  const { mobile } = useMediaQuery();
  const [showSearchEntityModal, setShowSearchEntityModal] = useState(false);
  const [selectedEntity, setSelectedEntity] = useState<Entity | null>(null);
  const [freshPrimer, setFreshPrimer] = useState<any>(null);

  const closeSearchEntityModal = useCallback(() => {
    setShowSearchEntityModal(false);
    setSelectedEntity(null);
  }, []);

  const openSearchEntityModal = useCallback(() => {
    setSelectedEntity(null);
    setShowSearchEntityModal(true);
  }, []);

  const onEntitySearchSelected = useCallback((entity: Entity) => {
    setSelectedEntity(entity);
  }, []);

  const goToSelectedEntityPrimer = useCallback(async () => {
    if (!selectedEntity?.slug) return;

    const data = await baseFetcher(primerInsightsUrl(selectedEntity.slug));
    const latest = data?.data?.[0];
    if (latest?.slug && latest?.publishedAt) {
      const oneYearAgo = new Date();
      oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
      if (new Date(latest.publishedAt) > oneYearAgo) {
        setShowSearchEntityModal(false);
        setSelectedEntity(null);
        if (mobile) {
          router.push(`/insights/${latest.slug}`);
        } else {
          setFreshPrimer(latest);
        }
        return;
      }
    }

    const destination = `/entities/${selectedEntity.slug}/research?tab=primer&request-primer=true`;
    setShowSearchEntityModal(false);
    setSelectedEntity(null);
    router.push(destination);
  }, [router, selectedEntity, mobile]);

  return (
    <>
      <OverlayTrigger
        placement="bottom"
        overlay={
          <Tooltip>
            <p className="mb-0">
              Create a concise company primer using Smartkarma AI
            </p>
          </Tooltip>
        }
      >
        <span
          className={twMerge(
            "cursor-pointer ",
            "text-[18px]",
            "space-x-[5px]",
            "hover:bg-[rgba(204,204,204,0.6)] px-[20px] py-[10px] rounded-full",
            className
          )}
          onClick={openSearchEntityModal}
        >
          <span>+ Primer</span>
        </span>
      </OverlayTrigger>

      {showSearchEntityModal && (
        <Modal
          isOpen={showSearchEntityModal}
          shouldCloseOnEsc
          onClose={closeSearchEntityModal}
          className={twMerge("md:w-[624px]")}
          overlayClassName=""
          onAfterOpen={() =>
            document.body.classList.add("!max-h-[calc(100vh-100px)]")
          }
          onAfterClose={() =>
            document.body.classList.remove("!max-h-[calc(100vh-100px)]")
          }
          fullMobilePage
        >
          <div className="p-[24px]">
            {/* Header */}
            <div className="flex justify-between items-start mb-[20px]">
              <div className="flex items-center gap-[10px]">
                <div className="w-[32px] h-[32px] flex items-center justify-center">
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img
                    src="https://assets.smartkarma.com/icons/sparkle.svg"
                    alt="AI Workflows"
                    width={20}
                    height={20}
                  />
                </div>
                <div>
                  <div className="text-[16px] font-semibold text-[#1f2937] leading-tight">
                    Generate a Company Primer
                  </div>
                  <div className="text-[12px] text-[#6b7280] mt-[2px]">
                    Powered by Smartkarma AI
                  </div>
                </div>
              </div>
            </div>

            {/* Company input */}
            <label className="block text-[13px] font-medium text-[#1f2937] mb-[6px]">
              Company
            </label>
            <SearchInput
              resultPlaceholder="Search by name or Bloomberg Ticker"
              placeholder={
                selectedEntity?.prettyName ||
                "Search by name or ticker (e.g. Tencent, 700 HK)"
              }
              endpoint="/api/v2/entities"
              queryParam="filter[fulltext]"
              onItemSelected={onEntitySearchSelected}
              className="w-full border-[1px] border-solid border-[#d1d5db] rounded-[6px] mb-[6px]"
              inputClassName={(isFocusing) =>
                twMerge(
                  isFocusing ? "" : "border-0",
                  "py-[10px] rounded-[6px] placeholder:text-[13px]"
                )
              }
              iconClassName={() => "text-[#9ca3af]"}
              dropdownClassName="rounded-b-[6px] top-[33px]"
              processData={({ data }) => ({
                data: data.filter((e: Entity) => !e.shouldHidePrimer),
              })}
            />
            <p className="text-[12px] text-[#6b7280] mb-[18px]">
              If a recent primer exists, we&apos;ll open it instead of
              regenerating.
            </p>

            {/* What's included */}
            <div className="bg-[#f9fafb] rounded-[6px] px-[14px] py-[12px] mb-[20px]">
              <div className="text-[12px] font-medium text-[#1f2937] mb-[8px]">
                Your primer will include
              </div>
              <div className="flex flex-col gap-[6px]">
                {[
                  "Business overview & competitive position",
                  "Financial summary & key metrics",
                  "Recent analyst commentary",
                ].map((item) => (
                  <div
                    key={item}
                    className="flex items-center gap-[8px] text-[12px] text-[#374151]"
                  >
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#1D9E75"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                    {item}
                  </div>
                ))}
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between gap-[12px]">
              <div className="flex items-center gap-[6px] text-[12px] text-[#6b7280]">
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#9ca3af"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="12" cy="12" r="10" />
                  <polyline points="12 6 12 12 16 14" />
                </svg>
                ~30-60 seconds
              </div>
              <div className="flex gap-[8px]">
                <button
                  type="button"
                  onClick={closeSearchEntityModal}
                  className="cursor-pointer rounded-full border border-solid border-[#d1d5db] bg-white px-[14px] py-[8px] text-[13px] font-medium text-[#374151] hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={goToSelectedEntityPrimer}
                  disabled={!selectedEntity}
                  className={twMerge(
                    "rounded-full border-none px-[16px] py-[8px] text-[13px] font-medium",
                    selectedEntity
                      ? "cursor-pointer bg-smart-teal-300 text-white hover:bg-dark-teal"
                      : "cursor-not-allowed bg-smart-teal/50 text-white opacity-50"
                  )}
                >
                  Generate primer
                </button>
              </div>
            </div>
            <div className="mt-[20px] border-0 border-t border-solid border-[#E5E7EB]">
              <RecentPrimersSection />
            </div>
          </div>
        </Modal>
      )}

      {freshPrimer && (
        <InsightModal
          slug={freshPrimer.slug}
          insight={freshPrimer}
          isOpen={!!freshPrimer}
          onClose={() => setFreshPrimer(null)}
        />
      )}
    </>
  );
};

export default EntityPrimerButton;
